import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    CircularProgress,
    Alert,
    Box,
    Grid,
    Typography,
    IconButton
} from '@mui/material';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import ClearIcon from '@mui/icons-material/Clear';
import { 
    useListChatGroups, 
    useSendChatMessage, 
    useUploadChatImage 
} from '../services/appService'; // Assuming hooks are exported from here
import { useAuth } from '../context/AuthContext'; // To get the token indirectly via hooks

function ChatGroupPostModal({ open, onClose, appId, initialText }) {
    const [broadcastGroups, setBroadcastGroups] = useState([]);
    const [selectedGroupId, setSelectedGroupId] = useState('');
    const [postText, setPostText] = useState('');
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [loadingGroups, setLoadingGroups] = useState(false);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState('');
    const imageInputRef = useRef(null);

    // Hooks for API calls
    const listChatGroups = useListChatGroups();
    const sendChatMessage = useSendChatMessage();
    const uploadChatImage = useUploadChatImage();

    // Effect to load broadcast groups when the modal opens
    useEffect(() => {
        if (open && appId) {
            setLoadingGroups(true);
            setError('');
            setBroadcastGroups([]); // Clear previous list
            setSelectedGroupId(''); // Reset selection
            listChatGroups(appId)
                .then(allGroups => {
                    const filteredGroups = allGroups.filter(g => g.type === 'broadcast');
                    setBroadcastGroups(filteredGroups || []);
                    if (filteredGroups.length > 0) {
                        // Optionally pre-select the first group?
                        // setSelectedGroupId(filteredGroups[0].id); 
                    } else {
                        setError("Keine Broadcast-Gruppen für diese App gefunden.");
                    }
                })
                .catch(err => {
                    console.error("Error fetching chat groups:", err);
                    setError(err.message || 'Fehler beim Laden der Chat-Gruppen.');
                })
                .finally(() => {
                    setLoadingGroups(false);
                });
        } else {
             // Clear state when closing
             setError('');
             setBroadcastGroups([]);
             setSelectedGroupId('');
             setPostText('');
             handleRemoveImage(); // Also clear image state
        }
    }, [open, appId, listChatGroups]);

    // Effect to update text field when initialText prop changes (after generation)
    useEffect(() => {
        if (initialText) {
            setPostText(initialText);
        }
    }, [initialText]);
    
     // Effect to revoke object URLs
    useEffect(() => {
        const previewUrl = imagePreview;
        return () => {
            if (previewUrl) {
                URL.revokeObjectURL(previewUrl);
            }
        };
    }, [imagePreview]);

    const handleFileSelect = (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        if (imagePreview) {
            URL.revokeObjectURL(imagePreview); // Revoke previous local preview
        }
        setImageFile(file);
        setImagePreview(URL.createObjectURL(file));
        setError(''); // Clear error on new file select
    };

    const handleRemoveImage = () => {
        if (imagePreview) {
            URL.revokeObjectURL(imagePreview);
        }
        setImageFile(null);
        setImagePreview(null);
        if (imageInputRef.current) {
            imageInputRef.current.value = null; // Reset file input
        }
    };

    const handleSendPost = async () => {
        if (!selectedGroupId) {
            setError("Bitte wählen Sie eine Broadcast-Gruppe aus.");
            return;
        }
        if (!postText && !imageFile) {
            setError("Der Beitrag muss Text oder ein Bild enthalten.");
            return;
        }

        setSending(true);
        setError('');
        let uploadedImageUrl = null;

        try {
            // 1. Upload image if selected
            if (imageFile) {
                const uploadResult = await uploadChatImage(appId, imageFile);
                uploadedImageUrl = uploadResult.url; // Assuming result has a 'url' property
            }

            // 2. Prepare message data
            const messageData = {
                text: postText || null,
                image_url: uploadedImageUrl,
                // group_id is handled by the endpoint URL in sendChatMessage hook
            };

            // 3. Send message using the hook
            await sendChatMessage(appId, selectedGroupId, messageData);

            // 4. Success: Close modal
            onClose(); 

        } catch (err) {
            console.error("Error sending chat group post:", err);
            setError(err.message || "Fehler beim Senden des Beitrags.");
        } finally {
            setSending(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
            <DialogTitle>Chatgruppen-Post erstellen</DialogTitle>
            <DialogContent dividers>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                <Grid container spacing={3}>
                    {/* Group Selection */}
                    <Grid item xs={12}>
                        <FormControl fullWidth disabled={loadingGroups || sending}>
                            <InputLabel id="broadcast-group-select-label">Broadcast-Gruppe wählen</InputLabel>
                            <Select
                                labelId="broadcast-group-select-label"
                                id="broadcast-group-select"
                                value={selectedGroupId}
                                label="Broadcast-Gruppe wählen"
                                onChange={(e) => setSelectedGroupId(e.target.value)}
                            >
                                {loadingGroups ? (
                                    <MenuItem disabled value="">
                                        <CircularProgress size={20} sx={{ mr: 1 }} /> Laden...
                                    </MenuItem>
                                ) : broadcastGroups.length === 0 ? (
                                     <MenuItem disabled value="">Keine Broadcast-Gruppen verfügbar</MenuItem>
                                ) : (
                                    broadcastGroups.map((group) => (
                                        <MenuItem key={group.id} value={group.id}>
                                            {group.name}
                                        </MenuItem>
                                    ))
                                )}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Post Text */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Beitragstext"
                            variant="outlined"
                            multiline
                            rows={6}
                            value={postText}
                            onChange={(e) => setPostText(e.target.value)}
                            disabled={sending}
                            placeholder="Schreiben Sie hier Ihren Beitrag..."
                        />
                    </Grid>

                    {/* Image Upload */}
                    <Grid item xs={12}>
                         <Typography variant="subtitle1" gutterBottom>Bild hinzufügen (Optional)</Typography>
                         <Box sx={{ display: 'flex', alignItems: 'center', border: '1px dashed grey', padding: 1, borderRadius: 1, minHeight: '74px' }}>
                              {sending && imageFile ? ( // Show progress only while sending an image
                                  <CircularProgress size={50} sx={{ mr: 1 }} />
                              ) : imagePreview ? (
                                  <img src={imagePreview} alt="Preview" style={{ width: '50px', height: '50px', objectFit: 'cover', marginRight: '8px', borderRadius: '4px' }} />
                              ) : (
                                  <Box sx={{ width: 50, height: 50, mr: 1, bgcolor: 'grey.200', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '4px' }}>
                                      <PhotoCamera color="disabled" />
                                  </Box>
                              )}
                              <Button
                                 variant="outlined"
                                 component="label"
                                 disabled={sending}
                                 startIcon={<PhotoCamera />}
                                 size="small"
                                 sx={{ mr: 1, flexShrink: 0 }}
                             >
                                 {imagePreview ? 'Bild ändern' : 'Bild auswählen'}
                                 <input
                                     ref={imageInputRef}
                                     type="file"
                                     hidden
                                     accept="image/*"
                                     onChange={handleFileSelect}
                                 />
                             </Button>
                              {imagePreview && (
                                  <IconButton
                                     size="small"
                                     onClick={handleRemoveImage}
                                     disabled={sending}
                                     sx={{ flexShrink: 0 }}
                                 >
                                     <ClearIcon />
                                 </IconButton>
                              )}
                          </Box>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={sending}>Abbrechen</Button>
                <Button 
                    onClick={handleSendPost} 
                    variant="contained" 
                    disabled={sending || !selectedGroupId || (!postText && !imageFile)}
                    sx={{ minWidth: 100 }}
                >
                    {sending ? <CircularProgress size={24} /> : 'Senden'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default ChatGroupPostModal; 